import { useCallback } from 'react';
import { setWorkspaces, setCurrentWorkspace, useWorkspaceStore } from '../store/workspaceStore';
import { setCollections, setSelectedRequest, setSelectedCollection, setSelectedFolder } from '../store/collectionsStore';
import { useAuthStore } from '../store/authStore';
import {
  setPublishModalWorkspace,
  useUiModalStore,
  setAuthModalMode,
  setAuthModalOpen,
  setPublishNetworkWorkspace,
  setActiveTab,
} from '../store/uiModalStore';
import { buildLocalManifestEntries } from '../utils/workspaceStorage';
import { canPublishTeamWorkspace } from '../utils/workspaceShareEligibility';
import { workspaceService } from '../services/workspaceService';
import { loadWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { saveLocalWorkspaceManifest, buildPublishLocalPayload } from '../services/localWorkspaceStore';

/** Publish a local workspace to the cloud, or to the API network. */
export function useWorkspacePublish({
  toastT,
  setPublishingWorkspaceId,
}) {
  /* ── Publish local → cloud ─────────────────────────────────────────────── */
  const requestPublishLocalWorkspace = useCallback((workspace) => {
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (workspace?.storage === 'local' && isAuthenticated) {
      setPublishModalWorkspace(workspace);
    }
  }, []);

  // No `visibility` argument: publish-local always creates a PRIVATE workspace
  // in the caller's own account, and the payload no longer has a knob for it.
  // Making a workspace public is a different, server-side, redacting route —
  // see the moduledoc in `services/workspacePublishPayload.js`.
  const publishLocalWorkspace = useCallback(async (workspace) => {
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (!workspace?.id || workspace.storage !== 'local' || !isAuthenticated) return;
    setPublishingWorkspaceId(workspace.id);
    try {
      const snap = await loadWorkspaceSnapshot(workspace);
      const payload = buildPublishLocalPayload(workspace, snap);
      const result = await workspaceService.publishLocal(payload);
      const cloudWs = result.workspace;
      const cols = result.collections || [];
      // Keep the local copy on disk — publishing ADDS a cloud copy.
      setWorkspaces((prev) => {
        const withTag = prev.map((w) =>
          String(w.id) === String(workspace.id)
            ? { ...w, published_cloud_id: cloudWs.id }
            : w
        );
        const merged = [...withTag, cloudWs];
        saveLocalWorkspaceManifest(buildLocalManifestEntries(merged)).catch(() => {});
        return merged;
      });
      setCurrentWorkspace(cloudWs);
      setCollections(cols);
      setSelectedRequest(null);
      setSelectedCollection(null);
      setSelectedFolder(null);
      toastT('toast.workspaceSynced', 'success', { name: cloudWs.name, visibility: 'private' });
      setPublishModalWorkspace(null);
    } catch (error) {
      console.error('publishLocalWorkspace failed:', error);
      toastT('toast.publishWorkspaceFailed', 'error', { message: error.message || '' });
      throw error;
    } finally {
      setPublishingWorkspaceId(null);
    }
  }, [toastT, setPublishingWorkspaceId]);

  const handlePublishLocalConfirm = useCallback(async () => {
    const publishModalWorkspace = useUiModalStore.getState().publishModalWorkspace;
    if (!publishModalWorkspace) return;
    try {
      await publishLocalWorkspace(publishModalWorkspace);
    } catch {
      // toast already shown
    }
  }, [publishLocalWorkspace]);

  /* ── Publish to network ────────────────────────────────────────────────── */
  const canPublishToNetwork = useCallback(
    (workspace) =>
      canPublishTeamWorkspace(workspace, useWorkspaceStore.getState().canPublishToNetworkPlan),
    []
  );

  const requestPublishToNetwork = useCallback((workspace) => {
    if (!workspace?.id) return;
    if (!useAuthStore.getState().isAuthenticated) {
      setAuthModalMode('login');
      setAuthModalOpen(true);
      return;
    }
    setPublishNetworkWorkspace(workspace);
  }, []);

  const handleNetworkImported = useCallback((cloudWs, cols) => {
    setWorkspaces((prev) => [...(prev || []), cloudWs]);
    setCurrentWorkspace(cloudWs);
    if (cols?.length) {
      setCollections(cols);
    }
    setSelectedRequest(null);
    setSelectedCollection(null);
    setSelectedFolder(null);
    setActiveTab('collections');
  }, []);

  return {
    requestPublishLocalWorkspace,
    handlePublishLocalConfirm,
    canPublishToNetwork,
    requestPublishToNetwork,
    handleNetworkImported,
  };
}
